import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import aboutData from "../../data/about.json";
import {
  Sparkles,
  ArrowRight,
  Users,
  Filter,
  ExternalLink,
} from "lucide-react";

import { FaLinkedin } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/api"; // Added explicit import to ensure safe endpoint parsing context

/* ─────────────────────────────────────────────
  MAGNETIC HOOK
───────────────────────────────────────────── */
const useMagnetic = (strength = 0.3) => {
  const ref = useRef(null);

  const onMove = useCallback(
    (e) => {
      if (!ref.current) return;
      const { left, top, width, height } = ref.current.getBoundingClientRect();
      const x = (e.clientX - left - width / 2) * strength;
      const y = (e.clientY - top - height / 2) * strength;
      gsap.to(ref.current, { x, y, duration: 0.4, ease: "power3.out" });
    },
    [strength],
  );

  const onLeave = useCallback(() => {
    if (!ref.current) return;
    gsap.to(ref.current, {
      x: 0,
      y: 0,
      duration: 0.6,
      ease: "elastic.out(1, 0.4)",
    });
  }, []);

  return { ref, onMove, onLeave };
};

/* ─────────────────────────────────────────────
  TEAM MEMBER CARD
───────────────────────────────────────────── */
const TeamCard = ({ member, index }) => {
  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const infoRef = useRef(null);
  const shineRef = useRef(null);
  const borderRef = useRef(null);

  const [isHovered, setIsHovered] = useState(false);
  const magnetic = useMagnetic(0.15);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  /* ── Scroll reveal ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        {
          y: 80,
          opacity: 0,
          rotateX: 8,
        },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          duration: 0.9,
          delay: index * 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 90%",
          },
        },
      );
    }, cardRef);

    return () => ctx.revert();
  }, [index]);

  /* ── 3D tilt on mouse move ── */
  const handleMove = (e) => {
    if (!innerRef.current) return;
    const { left, top, width, height } =
      innerRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;

    gsap.to(innerRef.current, {
      rotateY: x * 10,
      rotateX: -y * 10,
      duration: 0.5,
      ease: "power2.out",
    });

    /* Move shine */
    gsap.to(shineRef.current, {
      x: x * 80,
      y: y * 80,
      duration: 0.5,
      ease: "power2.out",
    });
  };

  const handleEnter = () => {
    setIsHovered(true);

    gsap.to(imgRef.current, {
      scale: 1.1,
      duration: 0.7,
      ease: "power2.out",
    });

    gsap.to(overlayRef.current, {
      opacity: 1,
      duration: 0.4,
      ease: "power2.out",
    });

    gsap.fromTo(
      infoRef.current?.children ?? [],
      { y: 20, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        stagger: 0.06,
        duration: 0.4,
        ease: "power3.out",
      },
    );

    gsap.to(borderRef.current, {
      opacity: 1,
      duration: 0.3,
    });
  };

  const handleLeave = () => {
    setIsHovered(false);

    gsap.to(innerRef.current, {
      rotateY: 0,
      rotateX: 0,
      duration: 0.6,
      ease: "power3.out",
    });

    gsap.to(imgRef.current, {
      scale: 1,
      duration: 0.5,
      ease: "power2.out",
    });

    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.4,
      ease: "power2.out",
    });

    gsap.to(borderRef.current, {
      opacity: 0,
      duration: 0.3,
    });
  };

  // Safe fallback if tags don't exist
  const expertiseTags = Array.isArray(member.tags) ? member.tags : [];
  // Use first tag as category badge or fallback safely
  const memberCategory = expertiseTags[0] || "Team Member";

  return (
    <div
      ref={cardRef}
      className="perspective-1000"
      style={{ opacity: 0 }}
      onMouseMove={(e) => {
        handleMove(e);
        magnetic.onMove(e);
      }}
      onMouseEnter={handleEnter}
      onMouseLeave={(e) => {
        handleLeave();
        magnetic.onLeave();
      }}
    >
      <div
        ref={innerRef}
        className="relative rounded-3xl overflow-hidden cursor-pointer will-change-transform transform-gpu group"
        style={{ aspectRatio: "3/4" }}
      >
        {/* ── Image (Updated member.photo mapping) ── */}
        <img
          ref={imgRef}
          src={
            member.photo
              ? `${backendUrl}${member.photo}`
              : "https://via.placeholder.com/300x400?text=No+Photo"
          }
          alt={member.name}
          className="absolute inset-0 w-full h-full object-cover will-change-transform transition-none"
          loading="lazy"
        />

        {/* ── Shine effect ── */}
        <div
          ref={shineRef}
          className="absolute w-48 h-48 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 60%)",
            top: "20%",
            left: "20%",
            filter: "blur(20px)",
          }}
        />

        {/* ── Base gradient overlay ── */}
        <div
          className="absolute inset-0"
          style={{
            background: `
  linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.3) 40%, transparent 70%),
  linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 20%)
`,
          }}
        />

        {/* ── Hover overlay ── */}
        <div
          ref={overlayRef}
          className="absolute inset-0"
          style={{
            background: `
  linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.6) 50%, rgba(29,78,216,0.15) 100%)
`,
            opacity: 0,
          }}
        />

        {/* ── Animated border glow ── */}
        <div
          ref={borderRef}
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            border: "1px solid rgba(59,130,246,0.4)",
            boxShadow:
              "0 0 30px rgba(59,130,246,0.1), inset 0 0 30px rgba(59,130,246,0.05)",
            opacity: 0,
          }}
        />

        {/* ── Category badge (top) ── */}
        <div className="absolute top-4 left-4 z-10">
          <span
            className="text-[9px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border border-gray-300"
            style={{
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              color: "rgba(75,85,99,0.8)",
            }}
          >
            {memberCategory}
          </span>
        </div>

        {/* ── Index number (top-right) ── */}
        <div className="absolute top-4 right-4 z-10">
          <span className="font-mono text-[10px] font-bold text-gray-400 tracking-widest">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        {/* ── Bottom info ── */}
        <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
          <h3 className="font-display font-bold text-gray-900 text-lg leading-snug">
            {member.name}
          </h3>
          <p className="text-primary-600 text-xs font-semibold tracking-wide mt-1">
            {member.role}
          </p>

          {/* Hover-reveal details */}
          <div
            ref={infoRef}
            className="mt-4 space-y-3 overflow-hidden"
            style={{
              maxHeight: isHovered ? "200px" : "0",
              opacity: isHovered ? 1 : 0,
              transition: "max-height 0.5s ease, opacity 0.3s ease",
            }}
          >
            <p className="text-gray-600 text-xs leading-relaxed">
              {member.bio}
            </p>

            {/* Expertise tags */}
            <div className="flex flex-wrap gap-1.5">
              {expertiseTags.map((tag, i) => (
                <span
                  key={i}
                  className="text-[9px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full"
                  style={{
                    background: "rgba(59,130,246,0.1)",
                    border: "1px solid rgba(59,130,246,0.2)",
                    color: "rgba(29,78,216,0.8)",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* LinkedIn Connection Access */}
            {member.linkedin && (
              <a
                href={member.linkedin}
                className="inline-flex items-center gap-2 text-[10px] font-semibold tracking-wider uppercase text-gray-500 hover:text-primary-600 transition-colors duration-300 group/link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedin size={12} />
                <span>Connect</span>
                <ExternalLink
                  size={9}
                  className="opacity-0 group-hover/link:opacity-100 -translate-x-1 group-hover/link:translate-x-0 transition-all duration-300"
                />
              </a>
            )}
          </div>
        </div>

        {/* ── Static border ── */}
        <div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{ border: "1px solid rgba(0,0,0,0.08)" }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
  FILTER PILL
───────────────────────────────────────────── */
const FilterPill = ({ label, isActive, onClick }) => {
  const pillRef = useRef(null);

  const handleClick = () => {
    if (isActive) return;
    gsap.fromTo(
      pillRef.current,
      { scale: 0.95 },
      { scale: 1, duration: 0.3, ease: "back.out(2)" },
    );
    onClick();
  };

  return (
    <button
      ref={pillRef}
      onClick={handleClick}
      className={`px-6 py-2.5 rounded-2xl text-[11px] font-bold tracking-[0.15em] transition-all duration-500 uppercase ${
        isActive
          ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/25 -translate-y-0.5"
          : "text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 border border-gray-200 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );
};

/* ─────────────────────────────────────────────
  MAIN TEAM OVERVIEW SECTION
───────────────────────────────────────────── */
const TeamOverview = () => {
  const { team: fallbackTeam } = aboutData; // Keep fallback copy for template copy text descriptions
  const [activeFilter, setActiveFilter] = useState("All");
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const limit = 20;

  // 1. Fetch live team data straight from backend server
  const {
    data: teamResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["teams", limit],
    queryFn: () =>
      api
        .get("/api/team/public", {
          params: {
            limit,
          },
        })
        .then((res) => res.data),
  });

  const membersList = teamResponse?.team || [];

  // 2. Generate categories dynamically based on the primary tags inside backend items
  useEffect(() => {
    if (membersList.length > 0) {
      const dynamicCategories = new Set(["All"]);
      membersList.forEach((m) => {
        if (Array.isArray(m.tags) && m.tags[0]) {
          dynamicCategories.add(m.tags[0]);
        }
      });
      setCategories(Array.from(dynamicCategories));

      // Initialize layout view context values
      if (activeFilter === "All") {
        setFilteredMembers(membersList);
      } else {
        setFilteredMembers(
          membersList.filter((m) => m.tags && m.tags[0] === activeFilter),
        );
      }
    }
  }, [teamResponse, activeFilter]);

  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const labelRef = useRef(null);
  const headRef = useRef(null);
  const lineRef = useRef(null);
  const descRef = useRef(null);
  const filterRef = useRef(null);
  const ctaRef = useRef(null);
  const bgRef = useRef(null);

  /* ── Header animation ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: headRef.current,
          start: "top 85%",
        },
      });

      tl.fromTo(
        headRef.current?.children ?? [],
        { y: 50, opacity: 0, skewY: 2 },
        {
          y: 0,
          opacity: 1,
          skewY: 0,
          stagger: 0.1,
          duration: 0.8,
          ease: "power4.out",
        },
      )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.5,
            ease: "power3.out",
            transformOrigin: "center",
          },
          "-=0.4",
        )
        .fromTo(
          descRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.3",
        )
        .fromTo(
          filterRef.current?.children ?? [],
          { y: 15, opacity: 0, scale: 0.9 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            stagger: 0.08,
            duration: 0.5,
            ease: "back.out(1.5)",
          },
          "-=0.3",
        );
    });

    return () => ctx.revert();
  }, []);

  /* ── Filter handler with GSAP timelines ── */
  const handleFilter = useCallback(
    (cat) => {
      if (cat === activeFilter || !gridRef.current) return;

      const items = gridRef.current.children;

      gsap.to(items, {
        scale: 0.9,
        opacity: 0,
        y: 20,
        filter: "blur(8px)",
        stagger: 0.04,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => {
          setActiveFilter(cat);
          const newItems =
            cat === "All"
              ? membersList
              : membersList.filter((m) => m.tags && m.tags[0] === cat);
          setFilteredMembers(newItems);
        },
      });
    },
    [activeFilter, membersList],
  );

  /* ── Re-animate after filter updates ── */
  useEffect(() => {
    if (!gridRef.current || filteredMembers.length === 0) return;
    const items = gridRef.current.children;

    requestAnimationFrame(() => {
      gsap.fromTo(
        items,
        { scale: 0.9, opacity: 0, y: 30, filter: "blur(8px)" },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          stagger: 0.08,
          duration: 0.5,
          ease: "power3.out",
        },
      );
    });
  }, [filteredMembers]);

  /* ── CTA and BG parallax hooks ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ctaRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 90%",
          },
        },
      );

      gsap.to(bgRef.current, {
        yPercent: 15,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="team-overview"
      className="relative overflow-hidden py-16"
      style={{
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)",
      }}
    >
      {/* ── Background Blobs Parallax ── */}
      <div ref={bgRef} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 dot-pattern opacity-[0.05]" />
        <div
          className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(29,78,216,0.08) 0%, transparent 60%)",
            filter: "blur(70px)",
          }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(245,158,11,0.06) 0%, transparent 60%)",
            filter: "blur(60px)",
          }}
        />
      </div>

      <div className="container-fluid relative z-20">
        {/* ── HEADER ── */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div ref={headRef} className="space-y-1 mt-5 overflow-hidden">
            <h2
              className="font-display font-black text-gray-900 leading-tight"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
            >
              {fallbackTeam.headline || "Meet Our Experienced"}
            </h2>
            <h2
              className="font-display font-black text-indigo-600 leading-tight"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
            >
              {fallbackTeam.headlineAccent || "Expert Team Members"}
            </h2>
          </div>

          <div
            ref={lineRef}
            className="h-[2px] w-20 mx-auto mt-5 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, #1d4ed8, #f59e0b, transparent)",
            }}
          />

          <p
            ref={descRef}
            className="text-gray-600 text-[15px] leading-relaxed mt-5 max-w-xl mx-auto"
          >
            {fallbackTeam.description ||
              "Discover the passionate individuals driving our research, engineering and creative initiatives forward."}
          </p>
        </div>

        {/* ── FILTER BAR ── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-12">
          <div ref={filterRef} className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <FilterPill
                key={cat}
                label={cat}
                isActive={activeFilter === cat}
                onClick={() => handleFilter(cat)}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 text-[10px] font-mono text-gray-600 tracking-widest uppercase">
            <Filter size={12} className="text-indigo-500/60" />
            <span>
              Showing{" "}
              <span className="text-gray-900 font-bold">
                {filteredMembers.length}
              </span>{" "}
              Members
            </span>
          </div>
        </div>

        {/* ── TEAM GRID DISPLAY STATES ── */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs font-mono tracking-wider">
              Syncing Team Profiles...
            </p>
          </div>
        ) : isError || filteredMembers.length === 0 ? (
          <div className="text-center py-20 text-gray-400 font-mono text-xs">
            No team profiles found for the selected view criteria.
          </div>
        ) : (
          <div
            ref={gridRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8"
          >
            {filteredMembers.map((member, i) => (
              <TeamCard key={member.id} member={member} index={i} />
            ))}
          </div>
        )}

        {/* ── CTA SECTION ── */}
        <div ref={ctaRef} className="mt-16 text-center">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl p-8 md:p-10 max-w-2xl mx-auto border border-gray-100 hover:border-indigo-500/20 transition-all duration-500 shadow-sm">
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              We're always looking for talented researchers, analysts, and
              development practitioners to join our growing team.
            </p>

            <Link
              to={fallbackTeam.cta?.href || "/careers"}
              className="group relative inline-flex items-center gap-2.5 px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-sm"
            >
              <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              <span>{fallbackTeam.cta?.text || "Join Our Team"}</span>
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamOverview;
